import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSoalInterpretasiGambar, hitungSkorInterpretasiGambar, soalInterpretasiGambar } from './bankSoalInterpretasiGambar';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

function soalDariMetaIG(meta) {
  const byId = {};
  soalInterpretasiGambar.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { ...byId[m.id] } : null)).filter(Boolean);
}

// ─── Konstanta ───────────────────────────────────────────────
const TOTAL_SOAL  = 8;
const WAKTU_TOTAL = 24 * 60; // 24 menit — cukup luang untuk menulis (~3 menit/gambar)
const MIN_PANJANG_KOLOM = 8;

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function kolomTerisiWajar(teks) {
  return typeof teks === 'string' && teks.trim().length >= MIN_PANJANG_KOLOM;
}

function BannerLanjutkanIG({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-indigo-400 bg-indigo-50">
      <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-indigo-800 mb-3">
        Baru sampai gambar <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-indigo-300 text-indigo-600 text-sm font-semibold hover:bg-indigo-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro ───────────────────────────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🖼️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Interpretasi Gambar</h1>
          <p className="text-gray-400 text-sm mt-1">
            {tryoutMode ? 'Bagian dari Tryout Resmi' : 'Latihan menulis cerita dari gambar situasi'}
          </p>
        </div>

        <BannerLanjutkanIG progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="rounded-xl p-4 mb-5 border-2 border-indigo-200 bg-indigo-50">
          <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 mb-1">📌 {tryoutMode ? 'Bagian Tryout' : 'Info Modul'}</p>
          <p className="text-sm text-indigo-800 leading-relaxed">
            Tiap gambar akan ditampilkan situasi yang ambigu (tidak jelas ceritanya). Tuliskan menurutmu: siapa tokoh utamanya, sedang melakukan apa, dan bagaimana perasaannya. Di bawah kolom isian ada <b>Contoh Jawaban</b> untuk membantumu terbiasa dengan formatnya.
          </p>
        </div>

        <div className="rounded-xl p-4 mb-5 border border-amber-200 bg-amber-50">
          <p className="text-[11px] text-amber-700 leading-relaxed">
            ⚠️ Latihan ini TIDAK menilai isi ceritamu (tidak ada jawaban benar/salah) — skor dihitung dari kelengkapan pengisian saja. Di tes psikologi sesungguhnya, jenis soal seperti ini dinilai oleh psikolog terlatih dari isi ceritanya.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">{TOTAL_SOAL}</p><p className="text-xs text-gray-500">Gambar</p></div>
          <div><p className="text-lg font-bold text-gray-800">{Math.round(WAKTU_TOTAL / 60)}'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">3</p><p className="text-xs text-gray-500">Kolom/gambar</p></div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition"
          >
            ← Kembali
          </button>
          <button
            onClick={onMulai}
            className="flex-grow py-3 rounded-xl font-bold border-2 border-indigo-400 bg-indigo-50 text-indigo-700 hover:opacity-90 transition"
          >
            Mulai Sekarang →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Kartu Contoh Jawaban ───────────────────────────────────────
function KartuContohJawaban({ contoh }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/60 p-4 mt-4">
      <p className="text-xs font-bold uppercase tracking-wide text-indigo-600 mb-2">💡 Contoh Jawaban (salah satu cara menjawab)</p>
      <div className="space-y-2 text-sm text-gray-600">
        <p><span className="font-semibold text-gray-700">Tokoh utama:</span> {contoh.tokoh}</p>
        <p><span className="font-semibold text-gray-700">Kegiatan:</span> {contoh.kegiatan}</p>
        <p><span className="font-semibold text-gray-700">Perasaan:</span> {contoh.perasaan}</p>
      </div>
      <p className="text-[10px] text-indigo-400 mt-2">Ini cuma contoh gambaran — jawabanmu sendiri boleh sangat berbeda, tidak ada yang salah.</p>
    </div>
  );
}

// ─── Layar Hasil ──────────────────────────────────────────────
function LayarHasil({ soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const hasil = hitungSkorInterpretasiGambar(soalList, jawabanUser);

  useEffect(() => {
    catatHasilTes('interpretasigambar', hasil.skor, {
      kolomTerisi: hasil.kolomTerisi, totalKolom: hasil.totalKolom,
      soalTerisiPenuh: hasil.soalTerisiPenuh, totalSoal: hasil.totalSoal,
    }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">🖼️</div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Hasil Interpretasi Gambar</h2>
          <p className="text-xs text-gray-400 mb-4">Skor dari kelengkapan pengisian, bukan penilaian isi cerita</p>
          <div className="text-6xl font-extrabold text-indigo-600 mb-1">{hasil.skor}</div>
          <p className="text-gray-400 text-sm mb-3">Skor Kelengkapan</p>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-2 mb-2">
            <div className="bg-indigo-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${hasil.skor}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-center text-xs mt-4">
            <div className="bg-indigo-50 rounded-lg py-2">
              <p className="font-bold text-indigo-700 text-base">{hasil.soalTerisiPenuh}/{hasil.totalSoal}</p>
              <p className="text-gray-500">Gambar terisi lengkap</p>
            </div>
            <div className="bg-gray-50 rounded-lg py-2">
              <p className="font-bold text-gray-600 text-base">{hasil.kolomTerisi}/{hasil.totalKolom}</p>
              <p className="text-gray-500">Kolom terisi</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4">
          <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
            <p className="text-[11px] text-amber-700 leading-relaxed">
              ⚠️ Latihan ini adalah simulasi non-klinis untuk membiasakan format menjawab — BUKAN penilaian atau diagnosis psikologis resmi. Isi ceritamu tidak dinilai benar/salah oleh sistem ini.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4 flex gap-3">
          <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">
            🔄 Ulangi
          </button>
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">
            ← Menu
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function InterpretasiGambarModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]           = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [soalList, setSoalList]   = useState([]);
  const [indeks, setIndeks]       = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(WAKTU_TOTAL);
  const timerRef                  = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('interpretasigambar', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'interpretasigambar', tryoutId, {
    soalMeta: soalList.map(s => ({ id: s.id })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  const handleMulai = useCallback(() => {
    hapusProgresTes('interpretasigambar', tryoutId);
    setProgresTersimpan(null);
    const soal = getSoalInterpretasiGambar().slice(0, TOTAL_SOAL);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, tokoh: '', kegiatan: '', perasaan: '' })));
    setWaktuSisa(WAKTU_TOTAL);
    setFase('tes');
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaIG(progresTersimpan.soalMeta);
    setSoalList(soal);
    setIndeks(progresTersimpan.indeks || 0);
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, tokoh: '', kegiatan: '', perasaan: '' })));
    setWaktuSisa(progresTersimpan.waktuSisa || WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('interpretasigambar', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          hapusProgresTes('interpretasigambar', tryoutId);
          setFase('hasil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [fase, tryoutId]);

  const handleUbahKolom = useCallback((field, nilai) => {
    setJawabanUser(prev =>
      prev.map(j => j.id === soalList[indeks].id ? { ...j, [field]: nilai } : j)
    );
  }, [indeks, soalList]);

  const handleNext = useCallback(() => {
    if (indeks < soalList.length - 1) setIndeks(i => i + 1);
    else { clearInterval(timerRef.current); hapusProgresTes('interpretasigambar', tryoutId); setFase('hasil'); }
  }, [indeks, soalList.length, tryoutId]);

  const handlePrev = useCallback(() => {
    if (indeks > 0) setIndeks(i => i - 1);
  }, [indeks]);

  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('interpretasigambar', tryoutId);
    setFase('hasil');
  }, [tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro');
    setSoalList([]);
    setIndeks(0);
    setJawabanUser([]);
    setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') return (
    <LayarIntro
      onMulai={handleMulai}
      onBack={onBack}
      tryoutMode={tryoutMode}
      progresTersimpan={progresTersimpan}
      onLanjutkan={handleLanjutkan}
      onMulaiBaru={handleMulaiBaruDariBanner}
    />
  );

  if (fase === 'hasil') {
    return (
      <LayarHasil
        tryoutMode={tryoutMode}
        tryoutId={tryoutId}
        soalList={soalList}
        jawabanUser={jawabanUser}
        onUlang={handleUlang}
        onBack={onBack}
      />
    );
  }

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id) || { tokoh: '', kegiatan: '', perasaan: '' };
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter      = waktuSisa < 120 ? 'bg-red-500' : waktuSisa < 360 ? 'bg-yellow-400' : 'bg-green-500';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg
              ${waktuSisa < 120 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              Gambar <span className="text-indigo-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">
              🖼️ Interpretasi
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4 flex items-center justify-center">
            <div className="w-full max-w-xs" style={{ aspectRatio: '200 / 140' }} dangerouslySetInnerHTML={{ __html: soal.gambar }} />
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1 block">1. Siapa tokoh utama dalam gambar ini?</label>
              <textarea
                value={jawabanSoalIni.tokoh}
                onChange={e => handleUbahKolom('tokoh', e.target.value)}
                rows={2}
                placeholder="Tuliskan menurutmu siapa tokoh utamanya..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-400 resize-none"
                style={{ color: '#111827', backgroundColor: '#FFFFFF' }}
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1 block">2. Sedang melakukan apa?</label>
              <textarea
                value={jawabanSoalIni.kegiatan}
                onChange={e => handleUbahKolom('kegiatan', e.target.value)}
                rows={2}
                placeholder="Tuliskan menurutmu kegiatan/kejadiannya..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-400 resize-none"
                style={{ color: '#111827', backgroundColor: '#FFFFFF' }}
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1 block">3. Bagaimana perasaannya?</label>
              <textarea
                value={jawabanSoalIni.perasaan}
                onChange={e => handleUbahKolom('perasaan', e.target.value)}
                rows={2}
                placeholder="Tuliskan menurutmu bagaimana perasaan tokohnya..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-400 resize-none"
                style={{ color: '#111827', backgroundColor: '#FFFFFF' }}
              />
            </div>
          </div>

          <KartuContohJawaban contoh={soal.contohJawaban} />
        </div>

        <div className="flex gap-3 mb-4">
          <button
            onClick={handlePrev}
            disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">
              Selanjutnya →
            </button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">
              Selesai ✓
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const j = jawabanUser.find(x => x.id === s.id) || {};
            const jumlahTerisi = ['tokoh', 'kegiatan', 'perasaan'].filter(f => kolomTerisiWajar(j[f])).length;
            const status = jumlahTerisi === 3 ? 'penuh' : jumlahTerisi > 0 ? 'sebagian' : 'kosong';
            return (
              <button
                key={s.id}
                onClick={() => setIndeks(i)}
                className={`w-8 h-8 text-xs rounded-lg font-semibold transition
                  ${i === indeks         ? 'bg-indigo-600 text-white'
                  : status === 'penuh'   ? 'bg-green-500 text-white'
                  : status === 'sebagian' ? 'bg-yellow-400 text-white'
                  : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟪 Aktif &nbsp;|&nbsp; 🟩 Terisi lengkap &nbsp;|&nbsp; 🟨 Sebagian &nbsp;|&nbsp; ⬜ Belum diisi
        </p>
      </div>
    </div>
  );
}
